import { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { FileText, DownloadSimple, Plus, Trash, UploadSimple, Sparkle } from "@phosphor-icons/react";

const CATEGORY_COLORS = {
  "Nutrition & Lifestyle": "text-[#84CC16] bg-[#84CC16]/10 border-[#84CC16]/30",
  "Workouts & Travel": "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30",
  "Progress Tracking": "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  "Onboarding & Legal": "text-[#F87171] bg-[#F87171]/10 border-[#F87171]/30",
};

const emptyForm = { title: "", category: "", description: "", file_id: null, filename: "" };

export default function ResourceTemplatesPanel() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/resources")
      .then(({ data }) => setResources(data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const uploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.file_id, filename: data.filename }));
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/resources", form);
      toast.success("Resource added to library");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save resource");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success("Resource deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete resource");
    }
  };

  const canSave = form.title && form.category && form.file_id;

  return (
    <div data-testid="admin-resources-panel">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-3xl text-white tracking-tight">Resource Library</h1>
          <p className="text-sm text-[#71717A] mt-1">Pre-built PDF templates ready to share with clients, plus your own custom guides.</p>
        </div>
        <Button data-testid="admin-upload-resource-button" onClick={openCreate} className="bg-[#D4AF37] text-black hover:bg-white rounded-full shrink-0">
          <Plus size={16} /> Upload Custom Guide
        </Button>
      </div>

      {loading ? (
        <p className="text-[#71717A]">Loading resources...</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {resources.map((r) => (
            <div
              key={r.id}
              data-testid={`resource-card-${r.id}`}
              className="bg-[#141414] border border-[#27272A] rounded-2xl p-6 flex flex-col hover:border-[#D4AF37]/40 transition-colors duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                  <FileText size={22} className="text-[#D4AF37]" weight="duotone" />
                </div>
                <div className="flex items-center gap-2">
                  {r.is_custom && (
                    <span data-testid={`resource-custom-badge-${r.id}`} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30">
                      <Sparkle size={10} weight="fill" /> Custom
                    </span>
                  )}
                  <span
                    data-testid={`resource-category-${r.id}`}
                    className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${CATEGORY_COLORS[r.category] || "text-[#A1A1AA] bg-white/5 border-[#27272A]"}`}
                  >
                    {r.category}
                  </span>
                </div>
              </div>
              <p className="font-heading text-lg text-white tracking-tight mb-2">{r.title}</p>
              <p className="text-sm text-[#A1A1AA] leading-relaxed flex-1 mb-5">{r.description}</p>
              <div className="flex items-center gap-3">
                <a
                  data-testid={`resource-download-button-${r.id}`}
                  href={fileUrl(r.file_id)}
                  download={r.filename}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-white text-black font-semibold text-sm px-5 py-3 rounded-full transition-colors duration-300"
                >
                  <DownloadSimple size={16} weight="bold" /> Download Sample PDF
                </a>
                {r.is_custom && (
                  <button
                    data-testid={`resource-delete-button-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="text-[#71717A] hover:text-red-400 transition-colors p-2"
                    title="Delete this custom guide"
                  >
                    <Trash size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {!resources.length && <p className="text-[#71717A]">No resource templates found.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="resource-upload-dialog" className="bg-[#141414] border-[#27272A] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Upload Custom Guide</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              data-testid="resource-title-input"
              placeholder="Title (e.g. Off-Season Recovery Protocol)"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            />
            <input
              data-testid="resource-category-input"
              placeholder="Category (e.g. Nutrition & Lifestyle)"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
              list="resource-category-suggestions"
            />
            <datalist id="resource-category-suggestions">
              {Object.keys(CATEGORY_COLORS).map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <textarea
              data-testid="resource-description-input"
              placeholder="Short description shown on the card"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            />
            <div className="border border-[#27272A] rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-white">PDF File</p>
                <p className="text-xs text-[#71717A]">{form.filename || "No file attached"}</p>
              </div>
              <label data-testid="resource-pdf-upload-label" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs px-4 py-2 rounded-full cursor-pointer transition-colors">
                <UploadSimple size={14} /> {uploading ? "Uploading..." : "Upload PDF"}
                <input data-testid="resource-pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} />
              </label>
            </div>
            <Button
              data-testid="resource-upload-submit-button"
              onClick={save}
              disabled={saving || !canSave}
              className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-full"
            >
              {saving ? "Saving..." : "Add to Resource Library"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
